import React, { createElement, createRef, forwardRef, Fragment, PureComponent, useEffect } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import memoizeOne from 'memoize-one';
import { ThemeContainer } from '../../../theme';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ContextMenu } from '../../ContextMenu';
import { useListContext } from '../../List/__internal__/ListContext';
import { getRTLOffsetType } from '../domHelpers';
import { TableRegions } from '../TableRegions';
import { getActualScrollLeft, VirtualScrollbar } from '../VirtualScrollbar';
import { Defaults, hasSelectionColumn } from './internal';
import { RowCellContent } from './RowCellContent';
import { SelectionCellContent } from './SelectionCellContent';
import { cancelTimeout, requestTimeout } from './timer';
import cn from '../Table.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
export const devMode = process.env.NODE_ENV !== 'production';
const IS_SCROLLING_DEBOUNCE_INTERVAL = 150;
const defaultItemKey = ({
  columnId,
  rowIndex
}) => `${rowIndex}:${columnId}`;

// copied from SelectionCellContent.tsx
const isRowSelected = (rowIndex, itemData) => {
  const {
    preference,
    getRowIdentifier,
    getSelectionState
  } = itemData;
  if (itemData.dragging === true) return false;
  const selectionStateOverride = getSelectionState?.(getRowIdentifier(rowIndex));
  return selectionStateOverride !== undefined ? !!selectionStateOverride : preference?.selectAll === true || !!preference?.selectedRows?.has(getRowIdentifier(rowIndex));
};
const getCellSelectionBorders = (itemData, rowIndex, colIndex) => {
  const rowAreaSelectionInfo = {
    prev: rowIndex === 0 ? false : isRowSelected(rowIndex - 1, itemData),
    curr: isRowSelected(rowIndex, itemData),
    next: rowIndex + 1 === itemData.rowCount ? false : isRowSelected(rowIndex + 1, itemData)
  };
  const columnCount = itemData.orderedColumnKeys?.size ?? itemData.columns.length;
  return {
    prev: rowAreaSelectionInfo.curr && colIndex === 0,
    next: rowAreaSelectionInfo.curr && colIndex === columnCount - 1,
    above: rowAreaSelectionInfo.curr === true && rowAreaSelectionInfo.prev !== true,
    below: rowAreaSelectionInfo.curr === true && rowAreaSelectionInfo.next !== true
  };
};

/**
 * Creates the containing element for a single row of the table (data or header)
 * @param {number} rowIndex the index of the row to create
 * @param {boolean} isHeader true if row is part of the table header, false otherwise
 */
const TableRowContainer = props => {
  const {
    testId,
    rowIndex,
    isHeader,
    isEmpty,
    className,
    style,
    children,
    getRowContainer,
    ariaSelected,
    ariaDisabled,
    disabledRow,
    readOnlyRow,
    rowRef,
    headerRef,
    addRowAttributes
  } = props;
  const styleClasses = cx(cn.row, className, {
    [cn.bodyRow]: !isHeader,
    [cn.emptyRow]: isEmpty === true,
    [cn.disabledRow]: disabledRow,
    [cn.readOnlyRow]: readOnlyRow
  });
  let rowProps = {
    className: styleClasses,
    role: 'row',
    'aria-disabled': ariaDisabled,
    style
  };
  if (addRowAttributes) {
    rowProps = {
      ...rowProps,
      ...addRowAttributes(rowIndex)
    };
  }
  if (getRowContainer) {
    rowProps = {
      'aria-rowindex': props.rowIndex + 1,
      'data-testid': postfixTestId(testId, `row${rowIndex + 1}`),
      'aria-selected': ariaSelected,
      ref: e => isHeader ? setRef(headerRef, e) : setRef(rowRef?.(rowIndex), e),
      ...rowProps
    };
    return /*#__PURE__*/_jsx(Fragment, {
      children: getRowContainer(rowIndex, children, rowProps)
    });
  }
  return /*#__PURE__*/_jsx("div", {
    ...rowProps,
    "aria-rowindex": !isHeader && rowIndex > 0 ? rowIndex : undefined,
    "data-testid": postfixTestId(testId, `row${rowIndex + 1}`),
    ref: e => isHeader ? setRef(headerRef, e) : setRef(rowRef?.(rowIndex), e),
    children: children
  });
};
function createRowCells(props) {
  const {
    rowIndex,
    isHeader,
    isEmpty,
    rowMetadata,
    frozenColumnCount
  } = props;
  const cells = [];

  // create row cells and add to row's cells array
  let columnIndex = 0; // include all frozen columns
  for (; columnIndex < frozenColumnCount; columnIndex++) {
    cells.push(props.createTableCell(columnIndex, rowIndex, isHeader, 1 /* all frozen columns will have span=1 */, isEmpty));
  }

  // offset of first non-frozen column would be width of all frozen columns
  const frozenColumnWidth = props.getColumnOffset(columnIndex);
  columnIndex = Math.max(frozenColumnCount, props.columnStartIndex);
  const rowOffset = props.getColumnOffset(columnIndex) - frozenColumnWidth;
  let colOffset = rowOffset;
  const getColumnSpan = props.getColumnSpan ?? 1;
  while (columnIndex <= props.columnStopIndex) {
    // check metadata to see if current columnIndex is claimed by a previous column span
    const cellMetadata = rowMetadata.getCell(columnIndex, getColumnSpan);
    const displayedColumn = cellMetadata?.displayed ?? columnIndex;

    // check if this column index is the one we should display
    if (displayedColumn === columnIndex) {
      const colSpan = isHeader ? 1 // TODO support colspan in header cells
      : typeof getColumnSpan === 'function' ? getColumnSpan(rowIndex, columnIndex) : getColumnSpan;
      if (colSpan > 1) {
        // update row metadata
        rowMetadata.setCell(columnIndex, {
          ...cellMetadata,
          // preserve any existing metadata
          displayed: columnIndex,
          colSpan
        });
      }
      const cell = props.createTableCell(columnIndex, rowIndex, isHeader, colSpan, isEmpty, colOffset);
      cells.push(cell);
      columnIndex += colSpan;
    } else {
      // or if another cell has superceded this one (has colSpan>1)
      const colSpan = cellMetadata?.colSpan ?? 1; // will have metadata and colspan, but lint can't tell that
      const indexToCreate = displayedColumn;
      if (indexToCreate < props.columnStartIndex) {
        colOffset = props.getColumnOffset(indexToCreate) - frozenColumnWidth;
      } else {
        colOffset = rowOffset;
      }
      const cell = props.createTableCell(indexToCreate, rowIndex, isHeader, colSpan, isEmpty, colOffset);
      cells.push(cell);
      columnIndex = indexToCreate + colSpan;
    }
  }
  return cells;
}

/**
 * Creates a single row of the table (data or header)
 * @param {number} rowIndex the index of the row to create
 * @param {boolean} isHeader true if row is part of the table header, false otherwise
 */
const TableRow = props => {
  const {
    rowIndex,
    isHeader,
    ariaDisabled,
    isEmpty,
    isRowDisabled,
    isRowReadOnly,
    rowRef,
    headerRef,
    testId
  } = props;
  const cells = createRowCells(props);
  const rowStyle = props.getRowStyle(rowIndex, props.columnStartIndex);
  const disabledRow = !!isRowDisabled?.(rowIndex);
  const readOnlyRow = !!isRowReadOnly?.(rowIndex);
  return /*#__PURE__*/_jsx(TableRowContainer, {
    testId: testId,
    rowIndex: rowIndex,
    isHeader: isHeader,
    isEmpty: isEmpty,
    style: rowStyle,
    ariaDisabled: ariaDisabled,
    disabledRow: isHeader ? false : disabledRow,
    readOnlyRow: isHeader ? false : readOnlyRow,
    rowRef: rowRef,
    headerRef: headerRef,
    children: cells
  });
};
export default function createGridComponent(config) {
  return class Grid extends PureComponent {
    _instanceProps = config.initInstanceProps(this.props, this);
    _resetIsScrollingTimeoutId = null;
    static defaultProps = {
      direction: 'ltr',
      itemData: undefined,
      useIsScrolling: false
    };
    state = {
      instance: this,
      isScrolling: false,
      horizontalScrollDirection: 'forward',
      scrollLeft: typeof this.props.initialScrollLeft === 'number' ? this.props.initialScrollLeft : 0,
      scrollTop: typeof this.props.initialScrollTop === 'number' ? this.props.initialScrollTop : 0,
      scrollUpdateWasRequested: false,
      verticalScrollDirection: 'forward',
      bodyClientHeight: null
    }; // Always use explicit constructor for React components.
    // It produces less code after transpilation. (#26)
    // eslint-disable-next-line no-useless-constructor

    constructor(props) {
      super(props);
      this._headerRef = /*#__PURE__*/createRef();
      this._bodyRef = /*#__PURE__*/createRef();
      this._bodyGroupRef = /*#__PURE__*/createRef();
    }
    static getDerivedStateFromProps(nextProps, prevState) {
      validateSharedProps(nextProps, prevState);
      config.validateProps(nextProps);
      return null;
    }

    /*
    function to return the row/col indices of the cell actually being displayed for any given row/col indices.
    only cases where a cell has a column or row span > 1 will the result not be the same as the input values.
    */
    getDisplayedCell({
      row,
      column,
      getColumnSpan
    }) {
      // check metadata to see if current columnIndex is claimed by a previous column span
      const rowMetadata = config.getRowMetadata(this._instanceProps, row);
      const cellMetadata = rowMetadata.getCell(column, getColumnSpan ?? 1);
      const displayedColumn = cellMetadata?.displayed ?? column;
      // why return row? we might do rowspan in the future...
      return {
        row,
        column: displayedColumn
      };
    }
    scrollTo({
      scrollLeft,
      scrollTop
    }) {
      if (scrollLeft !== undefined) {
        scrollLeft = Math.max(0, scrollLeft);
      }
      if (scrollTop !== undefined) {
        scrollTop = Math.max(0, scrollTop);
      }
      this.setState(prevState => {
        if (scrollLeft === undefined) {
          scrollLeft = prevState.scrollLeft;
        }
        if (scrollTop === undefined) {
          scrollTop = prevState.scrollTop;
        }
        if (prevState.scrollLeft === scrollLeft && prevState.scrollTop === scrollTop) {
          return null;
        }
        return {
          horizontalScrollDirection: prevState.scrollLeft < scrollLeft ? 'forward' : 'backward',
          scrollLeft,
          scrollTop,
          scrollUpdateWasRequested: true,
          verticalScrollDirection: prevState.scrollTop < scrollTop ? 'forward' : 'backward'
        };
      }, this._resetIsScrollingDebounced);
    }

    /**
     * Returns the normalized scrollLeft value
     */
    getScrollLeft() {
      return this.state.scrollLeft;
    }
    getActualScrollLeft() {
      const bodyRef = this._bodyGroupRef.current;
      if (!bodyRef) return this.state.scrollLeft;
      return getActualScrollLeft({
        direction: this.props.direction,
        clientWidth: bodyRef.clientWidth,
        scrollWidth: bodyRef.scrollWidth,
        normalizedScrollLeft: this.state.scrollLeft
      });
    }
    getScrollTop() {
      return this.state.scrollTop;
    }
    getCurrentColumnWidth(index) {
      return config.getColumnWidth(this.props, index, this._instanceProps);
    }
    getColumnIndexForOffset(scrollLeft) {
      const index = config.getColumnStartIndexForOffset(this.props, scrollLeft, this._instanceProps);
      return index;
    }
    getRowIndexForOffset(scrollTop) {
      const index = config.getRowStartIndexForOffset(this.props, scrollTop, this._instanceProps);
      return index;
    }
    scrollToItem({
      align = 'auto',
      columnIndex,
      rowIndex
    }) {
      const {
        columnCount,
        rowCount,
        width
      } = this.props;
      const {
        scrollLeft,
        scrollTop
      } = this.state;
      const scrollbarSize = getScrollbarSize();
      if (columnIndex !== undefined) {
        columnIndex = Math.max(0, Math.min(columnIndex, columnCount - 1));
      }
      if (rowIndex !== undefined) {
        rowIndex = Math.max(0, Math.min(rowIndex, rowCount - 1));
      }
      const estimatedTotalWidth = config.getEstimatedTotalWidth(this.props, this._instanceProps);
      // need to add 2 to the scrollbarSize to get the focus indicator to fully display
      const horizontalScrollbarSize = estimatedTotalWidth > width ? scrollbarSize + 2 : 0;
      const targetScrollLeft = columnIndex !== undefined ? config.getOffsetForColumnAndAlignment(this.props, columnIndex, align, scrollLeft, this._instanceProps,
      // pass in 0 for scroll bar size because the vertical scrollbar is
      // part of the last table column
      0) : scrollLeft;

      // NOVACOMMONS-13055: let's make sure the scroll makes sense before applying
      //  if the body has a vertical scrollbar present, then it will have a smaller clientWidth than the header.
      //  that state can make scrolling the body a harmless no-op, but the header may have enough room to scroll,
      //  causing a misalignment.
      const bodyGroupWidth = !!this._bodyGroupRef.current ? this._bodyGroupRef.current.clientWidth : width;
      const constrainedScrollLeft = Math.min(targetScrollLeft, estimatedTotalWidth - bodyGroupWidth);

      // to ensure it's fully visible.
      // But we only need to account for its size when it's actually visible.
      this.scrollTo({
        // only change scroll position if the target position is not already visible in the view
        scrollLeft: constrainedScrollLeft,
        scrollTop: rowIndex !== undefined ? config.getOffsetForRowAndAlignment(this.props, rowIndex, align, scrollTop, this._instanceProps, horizontalScrollbarSize) : scrollTop
      });
    }
    componentDidMount() {
      const {
        initialScrollLeft,
        initialScrollTop
      } = this.props;
      if (this._bodyRef.current !== null && typeof initialScrollTop === 'number') {
        const bodyRef = this._bodyRef.current;
        bodyRef.scrollTop = initialScrollTop;
      }
      if (this._bodyGroupRef.current !== null && typeof initialScrollLeft === 'number') {
        const bodyRef = this._bodyGroupRef.current;
        bodyRef.scrollLeft = initialScrollLeft;
      }
      this._callPropsCallbacks();
    }
    componentDidUpdate(prevProps) {
      const {
        direction,
        rowCount
      } = this.props;
      const {
        scrollTop,
        scrollUpdateWasRequested
      } = this.state;
      const didChangeDirection = direction !== prevProps.direction;
      const didGainRowsFromNone = rowCount > 0 && prevProps.rowCount === 0;
      if (scrollUpdateWasRequested || didChangeDirection || didGainRowsFromNone) {
        if (this._bodyGroupRef.current !== null) {
          // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
          // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
          // So we need to determine which browser behavior we're dealing with, and mimic it.
          const bodyRef = this._bodyGroupRef.current;
          const headerRef = this._headerRef.current;
          const actualScrollLeft = this.getActualScrollLeft();
          bodyRef.scrollLeft = actualScrollLeft;
          if (headerRef) headerRef.scrollLeft = actualScrollLeft;
        }
      }
      if (this._bodyRef.current !== null) {
        const bodyRef = this._bodyRef.current;
        bodyRef.scrollTop = Math.max(0, scrollTop);
      }
      this._callPropsCallbacks();
      const bodyClientHeight = this._bodyRef.current?.clientHeight || null;
      if (this.state.bodyClientHeight !== bodyClientHeight || this.state.isScrolling) {
        this.setState(prev => ({
          ...prev,
          bodyClientHeight
        }));
      }
      if (prevProps.density !== this.props.density) {
        // invalidate row size caches
        const reset = this.resetAfterRowIndex;
        if (typeof reset === 'function') {
          reset(0);
        }
      }
    }
    componentWillUnmount() {
      if (this._resetIsScrollingTimeoutId !== null) {
        cancelTimeout(this._resetIsScrollingTimeoutId);
      }
    }
    createTableCellContent = (columnIndex, rowIndex, isHeader) => {
      const {
        children,
        itemData,
        itemKey = defaultItemKey,
        useIsScrolling
      } = this.props;
      const {
        isScrolling
      } = this.state;
      return /*#__PURE__*/createElement(children, {
        columnIndex,
        data: itemData,
        key: itemKey({
          columnId: columnIndex,
          data: itemData,
          rowIndex
        }),
        isScrolling: useIsScrolling ? isScrolling : undefined,
        rowIndex,
        isHeader
      });
    }; // create the complete table cell for the given location including content and wrapper

    createTableCell = (columnIndex, rowIndex, isHeader, colSpan, isEmpty, offset) => {
      const {
        direction,
        itemData,
        onCellClick,
        testId,
        addCellAttributes
      } = this.props;
      const isRtl = direction === 'rtl';
      let frozenColumnCount = this.props.frozenColumnCount ?? 0;

      // calculate frozen column width
      let frozenColumnWidth = 0;
      for (let i = 0; i < frozenColumnCount; i++) {
        const cw = config.getColumnWidth(this.props, i, this._instanceProps);
        frozenColumnWidth += cw;
      }
      // TODO include single scrollable column width in calculation of needed width
      if (frozenColumnWidth >= this.props.width) {
        frozenColumnCount = 0; // TODO try to keep row selection header frozen if possible
      }

      const cell = isEmpty === true ? undefined : this.createTableCellContent(columnIndex, rowIndex, isHeader);
      const itemStyle = this._getItemStyle(rowIndex, columnIndex);
      if (colSpan > 1 && !isNullish(itemStyle.width)) {
        for (let i = 1; i < colSpan; i++) {
          const extraStyle = this._getItemStyle(rowIndex, columnIndex + 1);
          if (!isNullish(extraStyle.width) && !isNaN(extraStyle.width)) {
            itemStyle.width += extraStyle.width;
          }
        }
      }
      const columnOffset = config.getColumnOffset(this.props, columnIndex, this._instanceProps);
      itemStyle.width = config.getAdjustedColumnWidth(itemStyle.width ?? 0, columnIndex, columnOffset ?? offset ?? 0, this.props);
      if (offset !== undefined) {
        itemStyle.left = isRtl ? undefined : offset;
        itemStyle.right = isRtl ? offset : undefined;
      }
      const hasSelectionControl = hasSelectionColumn(itemData.selectionMode, itemData.columns.length, itemData.rowCount, itemData.hideSelectionControlColumn);
      if (isHeader) {
        delete itemStyle.height;
      }
      const {
        focusInfo,
        itemKey = defaultItemKey
      } = this.props;
      const isInFocus = !isNullish(focusInfo) && focusInfo.rowIndex === rowIndex && (focusInfo.colIndex === columnIndex || focusInfo.colIndex > columnIndex && focusInfo.colIndex < columnIndex + colSpan);
      const clickHandler = onCellClick ? event => onCellClick(rowIndex, columnIndex, event) : undefined;
      const emptyColumn = this.props.isLastColumnEmpty && columnIndex === this.props.columnCount - 1;
      let lastFrozenColumn = false;
      if (!hasSelectionControl) {
        lastFrozenColumn = columnIndex === frozenColumnCount - 1;
      } else {
        lastFrozenColumn = columnIndex > 0 && columnIndex === frozenColumnCount - 1;
      }
      let isResizing = false;
      if (itemData.columns && itemData.isColumnResizing) {
        const column = itemData.columns[columnIndex];
        isResizing = column && itemData.isColumnResizing(column.key);
      }
      const isRowHeader = itemData.columns[columnIndex]?.rowHeaderColumn;
      const role = isHeader ? 'columnheader' : isRowHeader ? 'rowheader' : 'gridcell';
      const testIdSuffix = isHeader ? `${role}-col${columnIndex}` : `${role}-row${rowIndex}-col${columnIndex}`;
      // GRIDCELL: the table's wrapper around consumer's content
      const rowIsSelected = isRowSelected(rowIndex, itemData);
      const cellBorderInfo = getCellSelectionBorders(itemData, rowIndex, columnIndex);

      // if whole table is not read only, but cells in this row are - set aria-readonly=true
      const rowKey = rowIndex !== -1 ? itemData.getRowIdentifier(rowIndex) : undefined;
      const isRowReadOnly = rowKey ? !!itemData.isRowReadOnly?.(rowKey) : false;
      const isBodyRow = isHeader !== true;
      const isTableModifiable = itemData.readOnly !== true;
      const ariaReadOnlyAttr = isBodyRow && isTableModifiable && isRowReadOnly ? {
        'aria-readonly': true
      } : {};
      const cellAttributes = addCellAttributes?.(rowIndex, columnIndex) ?? {};
      const pressedCellIsDataOrHeader = itemData.pressedCell?.region === TableRegions.Data || itemData.pressedCell?.region === TableRegions.RowHeader;
      const pressedCellInSameRow = rowIndex === itemData.pressedCell?.rowIndex;
      const pressedCellInSameColumn = itemData.pressedCell?.colIndex === columnIndex;
      const cellShouldShowPressedState = pressedCellIsDataOrHeader && pressedCellInSameRow && (itemData.pressEntireRow || pressedCellInSameColumn);
      return /*#__PURE__*/createElement('div', {
        className: cx(emptyColumn ? '' : cn.gridCell, cn.direction[direction], {
          [cn.headerRow]: isHeader,
          [cn.frozen]: columnIndex < frozenColumnCount,
          [cn.lastFrozenColumn]: lastFrozenColumn,
          [cn.fauxcus]: isInFocus,
          [cn.frozenResizing]: isResizing && columnIndex < frozenColumnCount,
          [cn.nonFrozenResizing]: isResizing && columnIndex >= frozenColumnCount,
          [cn.rowSeparator]: typeof this.props.showSeparator === 'function' ? this.props.showSeparator?.(rowIndex) : this.props.showSeparator,
          [cn.selected]: rowIsSelected,
          [cn.cellSelectionBorderPrev]: cellBorderInfo.prev,
          [cn.cellSelectionBorderNext]: cellBorderInfo.next,
          [cn.cellSelectionBorderAbove]: cellBorderInfo.above,
          [cn.cellSelectionBorderBelow]: cellBorderInfo.below,
          [cn.activate]: itemData.columns[columnIndex]?.onActivate && !isHeader,
          [cn.pressed]: cellShouldShowPressedState
        }),
        children: cell,
        role,
        'aria-colindex': columnIndex + 1,
        ...ariaReadOnlyAttr,
        'data-testid': postfixTestId(testId, testIdSuffix),
        key: itemKey({
          columnId: columnIndex,
          data: itemData,
          rowIndex
        }),
        ref: isInFocus ? this.props.focusCellRef : null,
        style: itemStyle,
        onMouseDown: clickHandler,
        ...cellAttributes
      });
    };
    getRowCells(rowIndex) {
      const {
        itemData,
        itemKey = defaultItemKey,
        testId
      } = this.props;
      const headerRowCount = this.props.headerRowCount ?? 1;
      const frozenColumnCount = this.props.frozenColumnCount ?? 0;
      const [columnStartIndex, columnStopIndex] = this._getHorizontalRangeToRender();
      const estimatedTotalWidth = config.getEstimatedTotalWidth(this.props, this._instanceProps);
      const bodyRowStyler = (rowIndex, columnStartIndex) => {
        const style = this._getRowStyle(rowIndex, columnStartIndex);
        return {
          ...style,
          width: estimatedTotalWidth
        };
      }; // create all visible table rows
      const hasSelectionControl = hasSelectionColumn(itemData.selectionMode, itemData.columns.length, itemData.rowCount, itemData.hideSelectionControlColumn);
      const row = /*#__PURE__*/createElement(TableRow, {
        testId,
        rowIndex,
        isHeader: false,
        headerRowCount,
        frozenColumnCount,
        columnStartIndex,
        columnStopIndex,
        createTableCell: this.createTableCell,
        getRowStyle: bodyRowStyler,
        getRowContainer: this.props.getRowContainer,
        dropDestinationIndex: this.props.dropDestinationIndex ?? -1,
        hasSelectionControl,
        getColumnSpan: this.props.columnSpan ?? 1,
        itemData: config,
        key: itemKey({
          columnId: 'H',
          data: itemData,
          rowIndex
        }),
        getColumnOffset: index => config.getColumnOffset(this.props, index, this._instanceProps),
        rowMetadata: config.getRowMetadata(this._instanceProps, rowIndex),
        rowRef: this.props.rowRef
      });
      return row;
    }
    render() {
      const {
        columnCount,
        direction,
        height,
        itemData,
        itemKey = defaultItemKey,
        outerElementType,
        rowCount,
        style,
        width,
        onCellClick,
        outerBorderVisible,
        testId,
        hideFillRows,
        removeFillRows
      } = this.props;
      const isRtl = direction === 'rtl';
      const [columnStartIndex, columnStopIndex] = this._getHorizontalRangeToRender();
      const [rowStartIndex, rowStopIndex] = this._getVerticalRangeToRender(this.state.bodyClientHeight ? Math.max(this.state.bodyClientHeight, height) : height);
      const itemRows = [];
      let frozenColumnCount = this.props.frozenColumnCount ?? 0;
      const headerRowCount = this.props.headerRowCount ?? 1;
      const headerRows = []; // create the cell content for an individual location w/in the table defined by (columnIndex, rowIndex)

      // calculate frozen column width
      let frozenColumnWidth = 0;
      for (let i = 0; i < frozenColumnCount; i++) {
        const cw = config.getColumnWidth(this.props, i, this._instanceProps);
        frozenColumnWidth += cw;
      }
      // TODO include single scrollable column width in calculation of needed width
      if (frozenColumnWidth >= this.props.width) {
        frozenColumnCount = 0; // TODO try to keep row selection header frozen if possible
      }

      if (columnCount > 0) {
        const headerVisible = this.props.headerVisible ?? true;
        const headerRowHeight = headerVisible ? this.props.headerHeight ?? 40 : 0;
        const headerRowStyler = (_rowIndex, _columnStartIndex) => {
          return {
            height: headerRowHeight,
            width: estimatedTotalWidth
          };
        };
        const hasSelectionControl = hasSelectionColumn(itemData.selectionMode, itemData.rowCount.length, itemData.rowCount, itemData.hideSelectionControlColumn);

        // create all table headers
        for (let rowIndex = -1, rowCount = 0;
        // header rows
        rowCount < headerRowCount; rowIndex--, rowCount++) {
          const row = /*#__PURE__*/createElement(TableRow, {
            testId,
            rowIndex,
            isHeader: true,
            ariaDisabled: itemData.disabled ? undefined : false,
            headerRowCount,
            frozenColumnCount,
            columnStartIndex,
            columnStopIndex,
            createTableCell: this.createTableCell,
            getRowStyle: headerRowStyler,
            dropDestinationIndex: this.props.dropDestinationIndex ?? -1,
            hasSelectionControl,
            getColumnSpan: this.props.columnSpan ?? 1,
            itemData: config,
            key: itemKey({
              columnId: 'H',
              data: itemData,
              rowIndex
            }),
            getColumnOffset: index => config.getColumnOffset(this.props, index, this._instanceProps),
            rowMetadata: config.getRowMetadata(this._instanceProps, rowIndex),
            headerRef: this.props.headerRef
          });
          headerRows.push(row);
        }
      }
      const headerHeight = (this.props.headerVisible !== false ? this.props.headerHeight ?? headerRows.map((_r, i) => this._getRowStyle(i, 0).height).reduce((x, y) => (x || 0) + (y || 0), 0) : 0) ?? 0;
      this._instanceProps.headerHeight = headerHeight;

      // Read this value AFTER items have been created,
      // So their actual sizes (if variable) are taken into consideration.
      const estimatedTotalHeight = config.getEstimatedTotalHeight(this.props, this._instanceProps);
      const estimatedTotalWidth = config.getEstimatedTotalWidth(this.props, this._instanceProps);
      const heightToFill = this.state.bodyClientHeight ?? height - headerHeight;
      const containsEmptyRows = rowCount === 0 || estimatedTotalHeight < heightToFill;
      const hasSelectionControl = hasSelectionColumn(itemData.selectionMode, itemData.columns.length, itemData.rowCount, itemData.hideSelectionControlColumn);
      const hasVisibleColumns = columnCount > (hasSelectionControl ? 1 : 0) + (this.props.isLastColumnEmpty ? 1 : 0);
      if (hasVisibleColumns && rowCount > 0) {
        // construct data rows for the visible area (if there are columns and rows to show)
        // use empty rows to fill in the remaining space
        const bodyRowStyler = (rowIndex, columnStartIndex) => {
          const style = this._getRowStyle(rowIndex, columnStartIndex);
          return {
            ...style,
            width: estimatedTotalWidth
          };
        };

        // create all visible table rows
        let usedHeight = 0;
        let rowIndex = rowStartIndex;
        const createTableCell = this.createTableCell;
        const getColumnSpan = this.props.columnSpan ?? 1;
        const defaultRenderRow = params => {
          // create the content for the row
          const cells = createRowCells({
            ...params,
            createTableCell,
            getColumnOffset: index => config.getColumnOffset(this.props, index, this._instanceProps),
            getColumnSpan: this.props.columnSpan,
            rowMetadata: config.getRowMetadata(this._instanceProps, params.rowIndex)
          });

          // create the row wrapped around the content
          const row = params.createRow({
            children: cells
          });
          return row;
        };

        // mostly a refactor of the 'createTableCell' function
        const createCellWithinRow = params => {
          const {
            rowIndex,
            rowOffset,
            rowMetadata,
            focusInfo,
            index,
            colSpan,
            content,
            className,
            style
          } = params;
          const cellAttributes = this.props.addCellAttributes?.(rowIndex, index) ?? {};
          const rowIsSelected = isRowSelected(rowIndex, itemData);
          const emptyColumn = this.props.isLastColumnEmpty && index === this.props.columnCount - 1;
          const isFrozen = index < frozenColumnCount;
          const lastFrozenColumn = hasSelectionControl ? index > 0 && index === frozenColumnCount - 1 : index === frozenColumnCount - 1;
          let isResizing = false;
          if (itemData.columns && itemData.isColumnResizing) {
            const column = itemData.columns[index];
            isResizing = column && itemData.isColumnResizing(column.key);
          }
          const itemStyle = this._getItemStyle(rowIndex, index);
          if (colSpan > 1 && !isNullish(itemStyle.width)) {
            for (let i = 1; i < colSpan; i++) {
              const extraStyle = this._getItemStyle(rowIndex, index + i);
              if (!isNullish(extraStyle.width) && !isNaN(extraStyle.width)) {
                itemStyle.width += extraStyle.width;
              }
            }
          }
          const offset = isFrozen ? 0 : rowOffset;
          if (offset !== undefined) {
            itemStyle.left = isRtl ? undefined : offset;
            itemStyle.right = isRtl ? offset : undefined;
          }
          if (colSpan > 1) {
            // update row metadata
            const cellMetadata = rowMetadata.getCell(index, getColumnSpan);
            rowMetadata.setCell(index, {
              ...cellMetadata,
              // preserve any existing metadata
              displayed: index,
              colSpan
            });
          }
          const isInFocus = !isNullish(focusInfo) && focusInfo.rowIndex === rowIndex && (focusInfo.colIndex === index || focusInfo.colIndex > index && focusInfo.colIndex < index + colSpan);
          const clickHandler = onCellClick ? event => onCellClick(rowIndex, params.index, event) : undefined;
          const cellBorderInfo = getCellSelectionBorders(itemData, rowIndex, index);
          const rowKey = rowIndex !== -1 ? itemData.getRowIdentifier(rowIndex) : undefined;
          const isRowReadOnly = rowKey ? !!itemData.isRowReadOnly?.(rowKey) : false;
          const isTableModifiable = itemData.readOnly !== true;
          const ariaReadOnlyAttr = isTableModifiable && isRowReadOnly ? {
            'aria-readonly': true
          } : {};
          return /*#__PURE__*/_createElement("div", {
            role: "gridcell",
            "aria-colindex": index + 1,
            ...ariaReadOnlyAttr,
            "data-testid": postfixTestId(testId, `gridcell-row${rowIndex}-col${index}`),
            className: cx(emptyColumn ? '' : cn.gridCell, cn.direction[direction], className, {
              [cn.frozen]: isFrozen,
              [cn.lastFrozenColumn]: lastFrozenColumn,
              [cn.fauxcus]: isInFocus,
              [cn.selected]: rowIsSelected,
              [cn.frozenResizing]: isResizing && index < frozenColumnCount,
              [cn.nonFrozenResizing]: isResizing && index >= frozenColumnCount,
              [cn.rowSeparator]: typeof this.props.showSeparator === 'function' ? this.props.showSeparator?.(rowIndex) : this.props.showSeparator,
              [cn.cellSelectionBorderPrev]: cellBorderInfo.prev,
              [cn.cellSelectionBorderNext]: cellBorderInfo.next,
              [cn.cellSelectionBorderAbove]: cellBorderInfo.above,
              [cn.cellSelectionBorderBelow]: cellBorderInfo.below
            }),
            style: {
              ...itemStyle,
              ...style
            },
            key: itemKey({
              columnId: index,
              data: itemData,
              rowIndex
            }),
            ref: isInFocus ? this.props.focusCellRef : null,
            onMouseDown: clickHandler,
            ...cellAttributes
          }, content);
        };

        // adjust rowStopIndex if rendered body clientHeight is greater than specified table height (likely CSS override from consumer)
        const adjustedRowStopIndex = (this.state.bodyClientHeight || 0) > (height ?? Defaults.sizes.tableHeight) ? rowStopIndex + 1 : rowStopIndex;
        const renderRow = this.props?.renderRow ?? defaultRenderRow;
        const {
          disabled,
          readOnly
        } = itemData;
        while (!hideFillRows && usedHeight < heightToFill || rowIndex < adjustedRowStopIndex) {
          let isEmpty = rowIndex >= rowCount;
          const getColumnOffset = index => config.getColumnOffset(this.props, index, this._instanceProps);
          const rowMetadata = config.getRowMetadata(this._instanceProps, rowIndex);
          const rowStyle = bodyRowStyler(rowIndex, columnStartIndex);

          // offset of first non-frozen column would be width of all frozen columns
          const frozenColumnWidth = getColumnOffset(frozenColumnCount);
          const rowOffset = getColumnOffset(Math.max(frozenColumnCount, columnStartIndex)) - frozenColumnWidth;
          const {
            focusInfo,
            isRowDisabled,
            isRowReadOnly
          } = this.props;
          const disabledRow = disabled || rowIndex > -1 && !!isRowDisabled?.(rowIndex);
          const readOnlyRow = readOnly || rowIndex > -1 && !!isRowReadOnly?.(rowIndex);
          const createCell = params => {
            return createCellWithinRow({
              ...params,
              rowIndex,
              rowOffset,
              rowMetadata,
              focusInfo
            });
          };
          const ignoreLast = itemData.dropEnabled && itemData.rowCount > 0 ? true : false;
          if (ignoreLast && rowIndex === itemData.rowCount) isEmpty = true;
          const row = renderRow({
            rowIndex,
            isEmpty,
            disabled: disabledRow,
            readOnly: readOnlyRow,
            frozenColumnCount,
            hasSelectionControl,
            columnStartIndex,
            columnStopIndex,
            getRowStyle: columnStartIndex => bodyRowStyler(rowIndex, columnStartIndex),
            createRow: params => {
              const {
                children,
                className,
                style,
                isHeader
              } = params;
              let ariaSelected = undefined;
              const rowSelected = (rowIndex, itemData) => {
                const {
                  getSelectionCheckboxState,
                  getSelectionState,
                  preference,
                  getRowIdentifier
                } = itemData;
                const checkboxState = getSelectionCheckboxState?.(getRowIdentifier(rowIndex));
                const selectionState = getSelectionState?.(getRowIdentifier(rowIndex));
                if (checkboxState === 'mixed' || selectionState === 'mixed') {
                  return false;
                }
                return checkboxState ?? selectionState ?? (preference?.selectAll === true || !!preference?.selectedRows?.has(getRowIdentifier(rowIndex)));
              };
              const isSelected = rowSelected(rowIndex, itemData);
              if (itemData.selectionMode === 'multiple' || itemData.selectionMode === 'desktop' || itemData.selectionMode === 'toggle') {
                ariaSelected = isSelected;
              }
              if (itemData.selectionMode === 'single' || itemData.selectionMode === 'singleRadio') {
                ariaSelected = isSelected ? true : undefined;
              }
              if (itemData.selectionMode === 'none') {
                ariaSelected = undefined;
              }

              // empty rows with the 'removeFillRows' flag enabled are null
              if (itemData.removeFillRows && isEmpty) {
                return null;
              }
              const rowContent = isEmpty ?
              /*#__PURE__*/
              // we will wrap the entire empty row in a context menu to support context menu activation on empty rows
              _jsx(ContextMenu, {
                onContextMenu: e => {
                  if (!e || disabledRow || itemData.hasContextMenu() !== true) return;
                  const rowKey = '';
                  const columnKey = '';
                  const contextMenuInfo = {
                    contextInfo: {
                      position: {
                        x: e.pageX,
                        y: e.pageY
                      },
                      cell: {
                        column: columnKey,
                        row: rowKey
                      }
                    },
                    // what position for full row? 0 is Data region, row index is clear, column index of -1 will not be used
                    position: {
                      region: 0,
                      rowIndex,
                      colIndex: -1
                    }
                  };
                  itemData.setContextMenuInfo(contextMenuInfo);
                  itemData.updateContextMenuItems(itemData.cellContextMenu, {
                    rowKey: rowKey || '',
                    columnKey
                  });
                  itemData.setContextMenuOpen(true);
                  itemData.storePreviousFocusIndicator(itemData.focusIndicator);
                },
                disabled: disabledRow,
                children: children
              }, itemKey({
                columnId: 'R',
                // 'R' is used for the 'row' container
                data: itemData,
                rowIndex
              })) : children;
              return /*#__PURE__*/_jsx(TableRowContainer, {
                testId: testId,
                rowIndex: rowIndex,
                isHeader: isHeader,
                isEmpty: isEmpty,
                className: className,
                style: {
                  ...rowStyle,
                  ...style
                },
                getRowContainer: this.props.getRowContainer,
                addRowAttributes: this.props.addRowAttributes,
                ariaSelected: ariaSelected,
                ariaDisabled: disabled ? undefined : disabledRow,
                disabledRow: disabledRow,
                readOnlyRow: readOnlyRow,
                rowRef: this.props.rowRef,
                headerRef: this.props.headerRef,
                children: rowContent
              }, itemKey({
                columnId: 'R',
                // 'R' is used for the 'row' container
                data: itemData,
                rowIndex
              }));
            },
            createCell: params => {
              // TODO should we use CellContent here? so many variations on a theme...
              return createCellWithinRow({
                ...params,
                content: params.content ? /*#__PURE__*/_jsx(RowCellContent, {
                  content: params.content,
                  itemData: itemData,
                  rowIndex: rowIndex,
                  columnIndex: params.index,
                  disabled: disabledRow,
                  readOnly: readOnlyRow
                }) : undefined,
                rowIndex,
                rowOffset,
                rowMetadata,
                focusInfo
              });
            },
            createSelectionCell: index => {
              return createCell({
                index,
                colSpan: 1,
                content: /*#__PURE__*/_jsx(SelectionCellContent, {
                  rowIndex: rowIndex,
                  itemData: itemData
                })
              });
            }
          });
          itemRows.push(row);
          usedHeight += rowStyle.height ?? Defaults.sizes.DEFAULT_ROW_HEIGHT;
          rowIndex++;
        }
      }
      const headerSection = this.props.headerVisible !== false ? /*#__PURE__*/createElement(ThemeContainer, {
        ...this.props.tableHeaderOverrideTheme,
        className: cn.headerSection
      }, /*#__PURE__*/createElement('div', {
        className: cn.headerContainer,
        role: 'rowgroup'
      }, /*#__PURE__*/createElement('div', {
        ref: this._headerRef,
        className: cn.rowGroup,
        style: {
          height: headerHeight,
          width
        }
      }, headerRows))) : null;
      const topRowOffset = this._getRowStyle(rowStartIndex, columnStartIndex).top ?? 0;
      const bodyRowGroup = /*#__PURE__*/createElement(ThemeContainer, {
        ...this.props.tableBodyOverrideTheme,
        ref: this._bodyGroupRef,
        className: cn.rowGroup,
        style: {
          top: topRowOffset,
          height: rowCount > 0 ? Math.max(Math.max(0, estimatedTotalHeight - topRowOffset), heightToFill) : Math.max(0, estimatedTotalHeight - topRowOffset),
          width,
          overflowY: containsEmptyRows ? 'hidden' : undefined
        }
      }, itemRows);
      const sectionProps = {
        ref: this._bodyRef,
        onScroll: this._onVScroll,
        className: cn.bodyContainer,
        role: 'rowgroup',
        tabIndex: 0,
        style: {}
      };
      const bodySection = this.props.getTableContainer ? this.props.getTableContainer(sectionProps, bodyRowGroup) : /*#__PURE__*/createElement('div', sectionProps, bodyRowGroup);
      const hasFrozenColumn = !hasSelectionControl ? frozenColumnCount > 0 : frozenColumnCount > 1;
      // offsetWidth is used here because clientWidth doesn't include space for the vertical scrollbar.
      // This causes the horizontal scrollbar container to be rendered whenever there is a vertical scrollbar
      // even when there is no need to scroll horizontally.
      const bodyWidth = Math.max(0, (this._bodyRef.current?.offsetWidth ?? 0) - frozenColumnWidth);
      const hbarSize = Math.round(Math.max(0, estimatedTotalWidth - frozenColumnWidth) - (outerBorderVisible === true ? 2 : 0));
      const hbarSection = columnCount > 0 && bodyWidth < hbarSize ? /*#__PURE__*/createElement(VirtualScrollbar, {
        className: cx(cn.hbar, hasSelectionControl && cn.hasBorder, hasFrozenColumn && cn.hasFrozenColumn),
        dir: 'horizontal',
        size: hbarSize,
        value: this.state.scrollLeft,
        onScroll: this._onHScroll,
        style: columnCount > 0 ? {
          marginInlineStart: `max(0px, calc(${frozenColumnWidth}px - ${hasFrozenColumn ? 'var(--table-frozen-column-divider-width)' : '1px'}))`
        } : undefined
      }) : undefined;
      const bodyHeight = Math.max(0, this._bodyRef.current?.offsetHeight ?? 0);
      // If the table has a header button, or if a vertical scrollbar is necessary, we need to increase
      // the padding on the last column cell to make room for those items but keep header and body widths
      // in sync.
      const lastColumnWidthAdjustment = this.props.headerButtonVisible || rowCount > 0 && bodyHeight < estimatedTotalHeight;
      const scrollbarSize = hbarSection ? getScrollbarSize() + 1 : 0;
      const gridHeight = Math.min(height, estimatedTotalHeight + headerHeight + (scrollbarSize + 3));
      return /*#__PURE__*/createElement(OuterContainer || outerElementType, {
        ref: this._outerRefSetter,
        className: outerBorderVisible === true ? cn.outerContainer : undefined,
        role: 'presentation',
        lastColumnWidthAdjustment,
        style: {
          position: 'relative',
          height: removeFillRows ? gridHeight : height,
          width,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          willChange: isRtl ? 'auto' : 'transform',
          flexBasis: '100%',
          direction,
          ['--table-last-column-pd-adjustment']: lastColumnWidthAdjustment ? 'var(--table-scroll-bar-space)' : '0px',
          ...style
        }
      }, headerSection, bodySection, hbarSection);
    }
    _callOnItemsRendered = memoizeOne((overscanColumnStartIndex, overscanColumnStopIndex, overscanRowStartIndex, overscanRowStopIndex, visibleColumnStartIndex, visibleColumnStopIndex, visibleRowStartIndex, visibleRowStopIndex, _dataKey) => this.props.onItemsRendered({
      overscanColumnStartIndex,
      overscanColumnStopIndex,
      overscanRowStartIndex,
      overscanRowStopIndex,
      visibleColumnStartIndex,
      visibleColumnStopIndex,
      visibleRowStartIndex,
      visibleRowStopIndex
    }));
    _callOnScroll = memoizeOne((scrollLeft, scrollTop, horizontalScrollDirection, verticalScrollDirection, scrollUpdateWasRequested) => this.props.onScroll({
      horizontalScrollDirection,
      scrollLeft,
      scrollTop,
      verticalScrollDirection,
      scrollUpdateWasRequested
    }));
    _callPropsCallbacks() {
      const {
        columnCount,
        onItemsRendered,
        onScroll,
        rowCount
      } = this.props;
      if (typeof onItemsRendered === 'function') {
        if (columnCount > 0 && rowCount > 0) {
          const [overscanColumnStartIndex, overscanColumnStopIndex, visibleColumnStartIndex, visibleColumnStopIndex] = this._getHorizontalRangeToRender();
          const [overscanRowStartIndex, overscanRowStopIndex, visibleRowStartIndex, visibleRowStopIndex] = this._getVerticalRangeToRender();
          this._callOnItemsRendered(overscanColumnStartIndex, overscanColumnStopIndex, overscanRowStartIndex, overscanRowStopIndex, visibleColumnStartIndex, visibleColumnStopIndex, visibleRowStartIndex, visibleRowStopIndex, this.props.dataKey);
        }
      }
      if (typeof onScroll === 'function') {
        const {
          horizontalScrollDirection,
          scrollLeft,
          scrollTop,
          scrollUpdateWasRequested,
          verticalScrollDirection
        } = this.state;
        this._callOnScroll(scrollLeft, scrollTop, horizontalScrollDirection, verticalScrollDirection, scrollUpdateWasRequested);
      }
    }
    _getEstimatedTotalWidth = () => {
      return config.getEstimatedTotalWidth(this.props, this._instanceProps);
    };
    _getEstimatedTotalHeight = () => {
      return config.getEstimatedTotalHeight(this.props, this._instanceProps);
    };
    _getRowMetadata(index) {
      return config.getRowMetadata(this._instanceProps, index);
    }
    _isScrolling() {
      return this.state.isScrolling;
    }
    _getRowStyle = (rowIndex, _columnStartIndex) => {
      const {
        rowHeight
      } = this.props;
      const itemStyleCache = this._getItemStyleCache(config.shouldResetStyleCacheOnItemSizeChange && rowHeight);
      const key = `body:${rowIndex}`;
      let style;
      if (itemStyleCache.hasOwnProperty(key)) {
        style = itemStyleCache[key];
      } else {
        const height = config.getRowHeight(this.props, rowIndex, this._instanceProps);
        itemStyleCache[key] = style = {
          top: config.getRowOffset(this.props, rowIndex, this._instanceProps),
          left: config.getColumnOffset(this.props, _columnStartIndex, this._instanceProps),
          height,
          minHeight: height ? height : 40
        };
      }
      return style;
    }; // Lazily create and cache item styles while scrolling,
    // So that pure component sCU will prevent re-renders.
    // We maintain this cache, and pass a style prop rather than index,
    // So that List can clear cached styles and force item re-render if necessary.
    _getItemStyle = (rowIndex, columnIndex) => {
      const {
        columnWidth,
        direction,
        rowHeight
      } = this.props;
      const itemStyleCache = this._getItemStyleCache(config.shouldResetStyleCacheOnItemSizeChange && columnWidth, config.shouldResetStyleCacheOnItemSizeChange && direction, config.shouldResetStyleCacheOnItemSizeChange && rowHeight);
      const key = `${rowIndex}:${columnIndex}`;
      let style;
      if (itemStyleCache.hasOwnProperty(key)) {
        style = itemStyleCache[key];
      } else {
        const offset = config.getColumnOffset(this.props, columnIndex, this._instanceProps);
        const isRtl = direction === 'rtl';
        const background = this.props.cellBackground ? this.props.cellBackground({
          rowIndex,
          columnIndex
        }) : 'inherit';
        itemStyleCache[key] = style = {
          left: isRtl ? undefined : offset,
          right: isRtl ? offset : undefined,
          height: config.getRowHeight(this.props, rowIndex, this._instanceProps),
          width: config.getColumnWidth(this.props, columnIndex, this._instanceProps),
          background
        };
      }
      return style;
    };
    _getItemStyleCache = memoizeOne(function (_, __, ___) {
      return {};
    });
    _setColumnWidth(index, width) {
      config.setColumnWidth(index, this.props, this._instanceProps, width);
    }
    _getHorizontalRangeRendered() {
      const {
        columnCount,
        overscanColumnCount
      } = this.props;
      const {
        horizontalScrollDirection,
        isScrolling,
        scrollLeft
      } = this.state;
      const overscanCountResolved = overscanColumnCount || 1;
      if (columnCount === 0) {
        return [0, 0, 0, 0];
      }
      const startIndex = config.getColumnStartIndexForOffset(this.props, scrollLeft, this._instanceProps, false);
      const stopIndex = config.getColumnStopIndexForStartIndex(this.props, startIndex, scrollLeft, this._instanceProps, false); // Overscan by one item in each direction so that tab/focus works.
      // If there isn't at least one extra item, tab loops back around.

      const overscanBackward = !isScrolling || horizontalScrollDirection === 'backward' ? Math.max(1, overscanCountResolved) : 1;
      const overscanForward = !isScrolling || horizontalScrollDirection === 'forward' ? Math.max(1, overscanCountResolved) : 1;
      return [Math.max(0, startIndex - overscanBackward), Math.max(0, Math.min(columnCount - 1, stopIndex + overscanForward)), startIndex, stopIndex];
    }
    _getHorizontalRangeToRender() {
      const {
        columnCount,
        overscanColumnCount
      } = this.props;
      const {
        horizontalScrollDirection,
        isScrolling,
        scrollLeft
      } = this.state;
      const overscanCountResolved = overscanColumnCount || 1;
      if (columnCount === 0) {
        return [0, 0, 0, 0];
      }
      const startIndex = config.getColumnStartIndexForOffset(this.props, scrollLeft, this._instanceProps);
      const stopIndex = config.getColumnStopIndexForStartIndex(this.props, startIndex, scrollLeft, this._instanceProps); // Overscan by one item in each direction so that tab/focus works.
      // If there isn't at least one extra item, tab loops back around.

      const overscanBackward = !isScrolling || horizontalScrollDirection === 'backward' ? Math.max(1, overscanCountResolved) : 1;
      const overscanForward = !isScrolling || horizontalScrollDirection === 'forward' ? Math.max(1, overscanCountResolved) : 1;
      return [Math.max(0, startIndex - overscanBackward), Math.max(0, Math.min(columnCount - 1, stopIndex + overscanForward)), startIndex, stopIndex];
    }
    _getVerticalRangeToRender(renderedHeight) {
      const {
        columnCount,
        overscanRowCount,
        rowCount
      } = this.props;
      const {
        isScrolling,
        verticalScrollDirection,
        scrollTop
      } = this.state;
      const overscanCountResolved = overscanRowCount || 1;
      if (columnCount === 0 || rowCount === 0) {
        return [0, 0, 0, 0];
      }
      let startIndex = config.getRowStartIndexForOffset(this.props, scrollTop, this._instanceProps);

      // adjust startIndex to account for decreased rowCount between renders
      if (startIndex >= rowCount) {
        startIndex = rowCount - 1;
      }

      // prefer renderedHeight to prop.height
      const props = !renderedHeight ? this.props : {
        ...this.props,
        height: renderedHeight
      };
      const stopIndex = config.getRowStopIndexForStartIndex(props, startIndex, scrollTop, this._instanceProps); // Overscan by one item in each direction so that tab/focus works.
      // If there isn't at least one extra item, tab loops back around.

      const overscanBackward = !isScrolling || verticalScrollDirection === 'backward' ? Math.max(1, overscanCountResolved) : 1;
      const overscanForward = !isScrolling || verticalScrollDirection === 'forward' ? Math.max(1, overscanCountResolved) : 1;
      return [Math.max(0, startIndex - overscanBackward), Math.max(0, stopIndex + overscanForward), startIndex, stopIndex];
    }
    _onHScroll = event => {
      const {
        clientWidth,
        scrollLeft,
        scrollTop,
        scrollWidth
      } = event.currentTarget;
      this.setState(prevState => {
        if (prevState.scrollLeft === scrollLeft && prevState.scrollTop === scrollTop) {
          // Scroll position may have been updated by cDM/cDU,
          // In which case we don't need to trigger another render,
          // And we don't want to update state.isScrolling.
          return null;
        }
        const {
          direction
        } = this.props; // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
        // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
        // It's also easier for this component if we convert offsets to the same format as they would be in for ltr.
        // So the simplest solution is to determine which browser behavior we're dealing with, and convert based on it.

        if (this._bodyGroupRef.current) {
          this._bodyGroupRef.current.scrollLeft = scrollLeft;
        }
        if (this._headerRef.current) {
          this._headerRef.current.scrollLeft = scrollLeft;
        }
        const normalizedScrollLeft = getNormalizedScrollLeft({
          scrollLeft,
          direction,
          scrollWidth,
          clientWidth
        });
        return {
          isScrolling: true,
          horizontalScrollDirection: prevState.scrollLeft < scrollLeft ? 'forward' : 'backward',
          scrollLeft: normalizedScrollLeft,
          scrollTop: prevState.scrollTop,
          verticalScrollDirection: prevState.scrollTop < scrollTop ? 'forward' : 'backward',
          scrollUpdateWasRequested: false
        };
      }, this._resetIsScrollingDebounced);
    };
    _onVScroll = event => {
      const {
        clientHeight,
        scrollLeft,
        scrollTop,
        scrollHeight
      } = event.currentTarget;
      this.setState(prevState => {
        if (prevState.scrollLeft === scrollLeft && prevState.scrollTop === scrollTop) {
          // Scroll position may have been updated by cDM/cDU,
          // In which case we don't need to trigger another render,
          // And we don't want to update state.isScrolling.
          return null;
        }
        const calculatedScrollLeft = prevState.scrollLeft;
        const calculatedScrollTop = Math.max(0, Math.min(scrollTop, scrollHeight - clientHeight));
        return {
          isScrolling: true,
          horizontalScrollDirection: prevState.scrollLeft < scrollLeft ? 'forward' : 'backward',
          scrollLeft: calculatedScrollLeft,
          scrollTop: calculatedScrollTop,
          verticalScrollDirection: prevState.scrollTop < scrollTop ? 'forward' : 'backward',
          scrollUpdateWasRequested: false
        };
      }, this._resetIsScrollingDebounced);
    };
    _outerRefSetter = ref => {
      const {
        outerRef
      } = this.props;
      this._outerRef = ref;
      if (typeof outerRef === 'function') {
        outerRef(ref);
      } else if (outerRef !== null && typeof outerRef === 'object' && outerRef.hasOwnProperty('current')) {
        outerRef.current = ref;
      }
    };
    _resetIsScrollingDebounced = () => {
      if (this._resetIsScrollingTimeoutId !== null) {
        cancelTimeout(this._resetIsScrollingTimeoutId);
      }
      this._resetIsScrollingTimeoutId = requestTimeout(this._resetIsScrolling, IS_SCROLLING_DEBOUNCE_INTERVAL);
    };
    _resetIsScrolling = () => {
      this._resetIsScrollingTimeoutId = null;
      this.setState({
        isScrolling: false
      }, () => {
        // Clear style cache after state update has been committed.
        // This way we don't break pure sCU for items that don't use isScrolling param.
        this._getItemStyleCache(-1);
      });
    };
  };
}
const validateSharedProps = ({
  children,
  direction,
  height,
  width
}, {}) => {
  if (devMode) {
    if (children === null) {
      throw Error('An invalid "children" prop has been specified. ' + 'Value should be a React component. ' + `"${children === null ? 'null' : typeof children}" was specified.`);
    }
    switch (direction) {
      case 'ltr':
      case 'rtl':
        // Valid values
        break;
      default:
        throw Error('An invalid "direction" prop has been specified. ' + 'Value should be either "ltr" or "rtl". ' + `"${direction}" was specified.`);
    }
    if (typeof width !== 'number') {
      throw Error('An invalid "width" prop has been specified. ' + 'Grids must specify a number for width. ' + `"${width === null ? 'null' : typeof width}" was specified.`);
    }
    if (typeof height !== 'number') {
      throw Error('An invalid "height" prop has been specified. ' + 'Grids must specify a number for height. ' + `"${height === null ? 'null' : typeof height}" was specified.`);
    }
  }
};
const getNormalizedScrollLeft = args => {
  const {
    scrollLeft,
    direction,
    scrollWidth,
    clientWidth
  } = args;
  if (direction === 'ltr') return Math.max(0, scrollLeft);
  let normalizedScrollLeft = scrollLeft;
  switch (getRTLOffsetType()) {
    case 'negative':
      normalizedScrollLeft = -scrollLeft;
      break;
    case 'positive-descending':
      normalizedScrollLeft = scrollWidth - clientWidth - scrollLeft;
      break;
  }
  normalizedScrollLeft = Math.max(0, Math.min(normalizedScrollLeft, scrollWidth - clientWidth));
  return normalizedScrollLeft;
};
const OuterContainer = /*#__PURE__*/forwardRef(function OuterContainer(props, ref) {
  const {
    children,
    lastColumnWidthAdjustment,
    ...restProps
  } = props;
  const setListContext = useListContext().setListContext;
  useEffect(() => {
    if (!setListContext) return;
    setListContext({
      tableHasVerticalScrollbar: lastColumnWidthAdjustment
    });
  }, [setListContext, lastColumnWidthAdjustment]);
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    ...restProps,
    children: children
  });
});
//# sourceMappingURL=createGridComponent.js.map